import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export function initHeader() {
    const header = document.querySelector('.site-header');
    const menuToggle = document.querySelector('.menu-toggle');
    const themeToggle = document.querySelector('.theme-toggle');
    const body = document.body;

    if (!header) return;

    // Header scroll animation
    ScrollTrigger.create({
        start: 'top -50',
        end: 99999,
        toggleClass: {
            className: 'is-scrolled',
            targets: '.site-header'
        }
    });

    // Menu toggle functionality
    if (menuToggle) {
        const toggleMenu = (forceClose = false) => {
            const isOpen = header.classList.contains('menu-open');

            if (forceClose || isOpen) {
                header.classList.remove('menu-open');
                menuToggle.classList.remove('is-active');
                body.style.overflow = '';
            } else {
                header.classList.add('menu-open');
                menuToggle.classList.add('is-active');
                body.style.overflow = 'hidden';
            }
        };

        menuToggle.addEventListener('click', () => toggleMenu());

        // Close menu when clicking on menu links
        const menuLinks = document.querySelectorAll('.menu-link');
        menuLinks.forEach(link => {
            link.addEventListener('click', () => toggleMenu(true));
        });

        // Initialize scramble effect on menu links
        initMenuScramble();

        // Initialize image hover effect on menu links
        initMenuImageHover();

        // Close menu on ESC key
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') toggleMenu(true);
        });
    }

    // Dark mode toggle
    if (themeToggle) {
        const savedTheme = localStorage.getItem('theme');

        // Dark mode is the default - only use light if explicitly saved
        if (savedTheme !== 'light') {
            body.classList.add('dark-mode');
        }

        themeToggle.addEventListener('click', () => {
            body.classList.toggle('dark-mode');
            const isDark = body.classList.contains('dark-mode');
            localStorage.setItem('theme', isDark ? 'dark' : 'light');
        });
    }
}

/**
 * Scramble effect for menu links on hover
 */
function initMenuScramble() {
    const menuLinks = document.querySelectorAll('.menu-link');
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';

    menuLinks.forEach(link => {
        const textSpan = link.querySelector('.menu-link-text');
        if (!textSpan) return;

        const originalText = link.dataset.text || textSpan.textContent;
        let isAnimating = false;
        let animationFrame;

        link.addEventListener('mouseenter', () => {
            if (isAnimating) return;
            isAnimating = true;

            let iteration = 0;
            const maxIterations = originalText.length * 2;

            const animate = () => {
                textSpan.textContent = originalText
                    .split('')
                    .map((char, index) => {
                        // Keep characters that have been "revealed"
                        if (index < iteration / 2) {
                            return originalText[index];
                        }
                        // Keep spaces
                        if (char === ' ') return ' ';
                        // Scramble the rest
                        return chars[Math.floor(Math.random() * chars.length)];
                    })
                    .join('');

                iteration++;

                if (iteration < maxIterations) {
                    animationFrame = requestAnimationFrame(animate);
                } else {
                    textSpan.textContent = originalText;
                    isAnimating = false;
                }
            };

            animate();
        });

        link.addEventListener('mouseleave', () => {
            cancelAnimationFrame(animationFrame);
            textSpan.textContent = originalText;
            isAnimating = false;
        });
    });
}

/**
 * Image hover effect for menu links
 * Changes the menu background image based on hovered link
 */
function initMenuImageHover() {
    const menuLinks = document.querySelectorAll('.menu-link[data-image]');
    const menuImage = document.querySelector('.menu-image img');

    if (!menuImage || menuLinks.length === 0) return;

    const defaultImage = menuImage.src;

    menuLinks.forEach(link => {
        const imageUrl = link.dataset.image;
        if (!imageUrl) return;

        // Preload image
        const preloadImg = new Image();
        preloadImg.src = imageUrl;

        link.addEventListener('mouseenter', () => {
            gsap.to(menuImage, {
                opacity: 0,
                scale: 1.05,
                duration: 0.2,
                ease: 'power2.in',
                onComplete: () => {
                    menuImage.src = imageUrl;
                    gsap.to(menuImage, {
                        opacity: 1,
                        scale: 1,
                        duration: 0.3,
                        ease: 'power2.out'
                    });
                }
            });
        });
    });

    // Reset to default image when leaving menu area
    const menuMain = document.querySelector('.menu-main');
    if (menuMain) {
        menuMain.addEventListener('mouseleave', () => {
            gsap.to(menuImage, {
                opacity: 0,
                scale: 1.05,
                duration: 0.2,
                ease: 'power2.in',
                onComplete: () => {
                    menuImage.src = defaultImage;
                    gsap.to(menuImage, {
                        opacity: 1,
                        scale: 1,
                        duration: 0.3,
                        ease: 'power2.out'
                    });
                }
            });
        });
    }
}
